import { FC, useEffect } from 'react'
import styled from 'styled-components'
import Button from 'components/molecules/Button'
import InputWithCaptions from 'components/molecules/InputWithCaptions'
import { ModalProps, ModalView } from 'components/organisms/Modal'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import { AppVersion } from '../../../@types'
import ReactSelect from 'components/molecules/ReactSelect'
import { GreyParagraph2 } from 'components/atoms/Paragraph'
import { AppArch, AppPlatform } from '@types'
import { useUpdateApplication } from '../useApplication'
import { useSearchParams } from 'react-router-dom'
import { getAppVersionsFn } from 'api'
import { useUserContext } from 'contexts/UserContext'

const osOptions: { label: string; value: AppPlatform }[] = [
  { label: 'iOS', value: 'IOS' },
  { label: 'Android', value: 'ANDROID' },
  { label: 'Windows', value: 'DESKTOP_WIN' },
  { label: 'MacOS', value: 'DESKTOP_MAC' },
  { label: 'Linux', value: 'DESKTOP_LINUX' },
]
const archOptions: { label: string; value: AppArch }[] = [
  { label: 'x64', value: 'x64' },
  { label: 'x86', value: 'x86' },
]

const EditApp: FC<ModalProps> = ({ onClose }) => {
  const [t] = useTranslation()
  const [searchParams] = useSearchParams()
  const id = Number(searchParams.get('id'))
  const { control, handleSubmit, reset } = useForm<AppVersion>({
    defaultValues: {
      platform: 'IOS',
      arch: 'x64',
      version_name: '',
      version_code: 0,
      download_url: '',
    },
  })
  const { state } = useUserContext()
  const { i_realm } = state?.realm
  const { data: apps } = useQuery(['app-versions', i_realm], getAppVersionsFn)
  const application = apps?.find((a) => a.id === id)
  const { mutate } = useUpdateApplication()

  useEffect(() => {
    if (application) {
      reset(application)
    }
  }, [application, reset])

  const onSubmit = (data: AppVersion) => {
    if (!application) return

    const payload = {
      ...application,
      ...data,
    }

    mutate(payload, {
      onSuccess: () => {
        onClose()
      },
    })
  }

  return (
    <ModalView title={t('modal.editApp.title')} onClose={onClose}>
      <Form onSubmit={handleSubmit(onSubmit, (errors) => console.log('FORM ERRORS', errors))}>
        <Field>
          <div>
            <GreyParagraph2 style={{ marginLeft: '15px', marginBottom: '5px' }} size="11" text={t('common.platform')} />

            <Controller
              name="platform"
              control={control}
              render={({ field: { ref, onChange, value, name, ...props } }) => {
                return (
                  <ReactSelect
                    value={osOptions.find((g) => g.value === value)}
                    onChange={({ value }) => {
                      onChange(value)
                    }}
                    options={osOptions}
                    placeholder={t('modal.addApp.chooseOs')}
                    {...props}
                  />
                )
              }}
            />
          </div>
          <div>
            <GreyParagraph2 style={{ marginLeft: '15px', marginBottom: '5px' }} size="11" text={t('common.arch')} />
            <Controller
              name="arch"
              control={control}
              render={({ field: { ref, onChange, value, name, ...props } }) => {
                return (
                  <ReactSelect
                    value={archOptions.find((g) => g.value === value)}
                    onChange={({ value }) => {
                      onChange(value)
                    }}
                    options={archOptions}
                    placeholder={t('modal.addApp.chooseArch')}
                    {...props}
                  />
                )
              }}
            />
          </div>
        </Field>

        <Field>
          <Controller name="id" control={control} render={({ field }) => <input type="hidden" {...field} />} />
          <Controller
            name="version_name"
            control={control}
            render={({ field: { ref, ...field } }) => (
              <InputWithCaptions {...field} placeholder="1.0.0" type="text" caption={t('common.version')} />
            )}
          />
          <Controller
            name="version_code"
            control={control}
            render={({ field: { ref, ...field } }) => (
              <InputWithCaptions {...field} placeholder="100" type="number" caption={t('common.versionCode')} />
            )}
          />
        </Field>
        <Field>
          <Controller
            name="download_url"
            control={control}
            render={({ field: { ref, ...field } }) => (
              <InputWithCaptions {...field} type="text" caption={t('common.downloadUrl')} />
            )}
          />
        </Field>

        <div style={{ display: 'flex', alignItems: 'right' }}>
          <Button style={{ alignSelf: 'flex-end' }} appearance="blue" type="submit">
            {t('modal.editApp.submit')}
          </Button>
          <Button style={{ alignSelf: 'flex-end' }} type="button" onClick={onClose}>
            {t('common.cancel')}
          </Button>
        </div>
      </Form>
    </ModalView>
  )
}

const Form = styled.form`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  width: 100%;
`

const Field = styled.div`
  display: flex;
  margin-bottom: 16px;
  flex-flow: wrap;
  gap: 12px;
  & > * {
    flex: 1;
    min-width: 180px;
  }
`

export default EditApp
